import React from 'react';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import { AlertTriangle, Lightbulb, Sparkles, FileText, Trash2, Pencil } from 'lucide-react';
import ScoreMeters from '../ScoreMeters';
import EvaluationTagChips from '../EvaluationTagChips';
import { extractTagEvaluation, hasLegacyNumericScores } from '../../utils/evaluationDisplay';
import AiUnreadDot from '../AiUnreadDot';
import { useAiUnread } from '../../contexts/AiUnreadContext';
import { hasLensItemAiAnalysis } from '../../utils/aiAnalysisPresence';
import { useTranslation } from '../../hooks/useTranslation.jsx';
import { FormCheckOwnIndicator } from '../InputFeedbackIndicator';

const BRAND_COLORS = {
  obstacle: '#E94B6E',
  opportunity: '#A6D64D',
};

const Card = styled.div`
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid ${props => props.$borderColor};
  border-left: 3px solid ${props => props.$borderColor};
  border-radius: 12px;
  padding: 14px 16px;
  transition: all 0.2s ease;
  cursor: ${props => props.$clickable ? 'pointer' : 'default'};

  &:hover {
    background: rgba(255, 255, 255, 0.07);
    border-color: ${props => props.$borderColor};
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 6px;
`;

const TypeIcon = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 8px;
  background: ${props => props.$color}22;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.$color};
  flex-shrink: 0;
`;

const Title = styled.h4`
  margin: 0;
  font-size: 0.9rem;
  font-weight: 600;
  color: #fff;
  line-height: 1.3;
  flex: 1;
`;

const Actions = styled.div`
  display: flex;
  gap: 4px;
  flex-shrink: 0;
  opacity: 0;
  transition: opacity 0.15s ease;

  ${Card}:hover & {
    opacity: 1;
  }
`;

const ActionButton = styled.button`
  width: 28px;
  height: 28px;
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.08);
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: rgba(255, 255, 255, 0.6);
  transition: all 0.15s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.15);
    color: #fff;
  }
`;

const Description = styled.p`
  margin: 0 0 8px 0;
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.6);
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const Meta = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
`;

const Badge = styled.span`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  border-radius: 6px;
  font-size: 0.7rem;
  font-weight: 500;
  background: ${props => props.$bg || 'rgba(255, 255, 255, 0.08)'};
  color: ${props => props.$color || 'rgba(255, 255, 255, 0.6)'};
`;

const shimmer = keyframes`
  0% { background-position: -200px 0; }
  100% { background-position: 200px 0; }
`;

const EnrichingBar = styled.div`
  margin-top: 8px;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 10px;
  border-radius: 6px;
  background: rgba(168, 85, 247, 0.08);
  border: 1px solid rgba(168, 85, 247, 0.15);
  font-size: 0.7rem;
  color: rgba(168, 85, 247, 0.8);

  .shimmer {
    flex: 1;
    height: 6px;
    border-radius: 3px;
    background: linear-gradient(90deg, rgba(168,85,247,0.1) 0%, rgba(168,85,247,0.3) 50%, rgba(168,85,247,0.1) 100%);
    background-size: 200px 100%;
    animation: ${shimmer} 1.5s infinite;
  }

  html[data-platform='tizen'] & .shimmer {
    animation: none;
    background: rgba(168, 85, 247, 0.25);
    background-size: auto;
  }
`;

const SOURCE_CONFIG = {
  manual: { icon: FileText, bg: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.5)' },
  ai: { label: 'AI', icon: Sparkles, bg: 'rgba(168,85,247,0.18)', color: '#C084FC' },
  rag: { label: 'RAG', icon: FileText, bg: 'rgba(96,165,250,0.15)', color: '#93C5FD' },
};

/**
 * LensItemCard - Displays an obstacle or opportunity within a lens view.
 * Color-coded: red for obstacles, green for opportunities.
 * AI items show two meters: research support (-100..100) and quality (0..100).
 */
export default function LensItemCard({
  item,
  type = 'obstacle',
  enriching = false,
  onClick,
  onEdit,
  onDelete,
  showDescription = true,
  currentDeviceId = null,
  formCheckPending = false,
  formCheckDismissed = false,
  onDismissFormCheck,
}) {
  const { t } = useTranslation();
  const color = BRAND_COLORS[type] || BRAND_COLORS.obstacle;
  const sourceKey = item?.source || 'manual';
  const source = SOURCE_CONFIG[sourceKey] || SOURCE_CONFIG.manual;
  const sourceLabel = sourceKey === 'manual' || !source.label
    ? t('lenses.sourceManual')
    : source.label;
  const SourceIcon = source.icon;
  const TypeIconComponent = type === 'obstacle' ? AlertTriangle : Lightbulb;
  // Only show edit/delete for own items (or if created_by is not set — legacy items)
  const isOwn = !item?.created_by || item.created_by === currentDeviceId;

  // Parse scores from rag_references JSONB (stored as { references, scores })
  const scores = item?.rag_references?.scores || null;
  const tagEval = extractTagEvaluation(scores);
  const legacyScores = !tagEval && hasLegacyNumericScores(scores) ? scores : null;
  const { isUnread } = useAiUnread();
  const showUnreadDot = isOwn
    && !enriching
    && isUnread('lens_item', item?.id, hasLensItemAiAnalysis(item));

  return (
    <AiUnreadDot visible={showUnreadDot}>
    <FormCheckOwnIndicator
      isOwn={isOwn}
      formCheck={item?.rag_references?.formCheck}
      pending={formCheckPending}
      dismissed={formCheckDismissed}
      onDismiss={onDismissFormCheck}
      variant="border"
      fill
      size="sm"
    >
    <Card $borderColor={color} $clickable={!!onClick} onClick={onClick}>
      <Header>
        <TypeIcon $color={color}>
          <TypeIconComponent size={16} />
        </TypeIcon>
        <Title>{item?.title || t('lenses.untitled')}</Title>
        <Actions>
          {onEdit && isOwn && (
            <ActionButton onClick={e => { e.stopPropagation(); onEdit(item); }} title={t('common.edit')}>
              <Pencil size={14} />
            </ActionButton>
          )}
          {onDelete && isOwn && (
            <ActionButton onClick={e => { e.stopPropagation(); onDelete(item); }} title={t('common.delete')}>
              <Trash2 size={14} />
            </ActionButton>
          )}
        </Actions>
      </Header>

      {showDescription && item?.description && (
        <Description>{item.description}</Description>
      )}

      <Meta>
        <Badge $bg={source.bg} $color={source.color}>
          <SourceIcon size={10} />
          {sourceLabel}
        </Badge>
        {isOwn && item?.created_by && (
          <Badge $bg="rgba(59, 130, 246, 0.15)" $color="#93c5fd">{t('lenses.own')}</Badge>
        )}
      </Meta>

      {enriching && (
        <EnrichingBar>
          <Sparkles size={12} />
          {t('lenses.aiAnalyzing')}
          <div className="shimmer" />
        </EnrichingBar>
      )}
      {tagEval && !enriching && (
        <div style={{ marginTop: 8 }} onClick={e => e.stopPropagation()}>
          <EvaluationTagChips
            tags={tagEval.tags}
            contradictsResearch={tagEval.contradicts_research}
            researchStance={tagEval.research_stance}
            compact
            noTopMargin
          />
        </div>
      )}
      {legacyScores && !enriching && (
        <div style={{ marginTop: 8 }} onClick={e => e.stopPropagation()}>
          <ScoreMeters scores={legacyScores} size="compact" />
        </div>
      )}
    </Card>
    </FormCheckOwnIndicator>
    </AiUnreadDot>
  );
}
